import { AnimationMixer, Bone, LoopRepeat, PropertyBinding, Quaternion, Vector3 } from "three";

export const AVATAR_CAPABILITIES_VERSION = 1;

export const ANIMATION_SELECTIONS = {
  EMBEDDED: "embedded",
  LEGACY_FEMALE: "legacy-female",
  LEGACY_MALE: "legacy-male",
  NONE: "none"
};

export const REQUIRED_ANIMATION_ACTIONS = ["idle", "walkForward"];
export const OPTIONAL_ANIMATION_ACTIONS = [
  "walkBackward",
  "walkLeft",
  "walkRight",
  "runForward",
  "runBackward",
  "runLeft",
  "runRight",
  "fly",
  "sitLow",
  "sitMedium"
];

const sourceExtensionsByRoot = new WeakMap();

export function setAvatarCapabilitiesMetadata(root, metadata) {
  root.traverse(node => {
    if (Object.prototype.hasOwnProperty.call(node.userData || {}, "avatarCapabilities")) {
      delete node.userData.avatarCapabilities;
    }
  });
  root.userData.avatarCapabilities = metadata;
}

function sourceExtensionsFor(root) {
  let source = root;
  while (source) {
    const extensions = sourceExtensionsByRoot.get(source);
    if (extensions) return extensions;
    source = source.parent;
  }
  return undefined;
}

const ANIMATION_ALIASES = {
  idle: ["Idle"],
  walkForward: ["Walking", "Walk", "WalkForward"],
  walkBackward: ["WalkingBackwards", "WalkBackward"],
  walkLeft: ["LeftStrafeWalk", "WalkLeft"],
  walkRight: ["RightStrafeWalk", "WalkRight"],
  runForward: ["Running", "Run", "RunForward"],
  runBackward: ["RunningBackward", "RunBackward"],
  runLeft: ["LeftStrafe", "RunLeft"],
  runRight: ["RightStrafe", "RunRight"],
  fly: ["Flying", "Fly"],
  sitLow: ["SitLowSeat", "SitLow"],
  sitMedium: ["SitMediumSeat", "SitMedium"]
};

const RUNTIME_ANIMATION_CLIP_NAMES = {
  idle: "Idle",
  walkForward: "Walking",
  walking2: "walking",
  walkBackward: "WalkingBackwards",
  walkLeft: "LeftStrafeWalk",
  walkRight: "RightStrafeWalk",
  runForward: "Running",
  runBackward: "RunningBackward",
  runLeft: "LeftStrafe",
  runRight: "RightStrafe",
  fly: "Flying",
  sitLow: "SitLowSeat",
  sitMedium: "SitMediumSeat"
};

const HUMANOID_ROLES = {
  hips: { canonical: ["Hips"], aliases: ["Pelvis"] },
  spine: { canonical: ["Spine"], aliases: ["Spine1"] },
  chest: { canonical: ["Spine2"], aliases: ["Chest", "UpperChest"] },
  neck: { canonical: ["Neck"], aliases: [] },
  head: { canonical: ["Head"], aliases: [] },
  leftEye: { canonical: ["LeftEye"], aliases: ["EyeLeft"] },
  rightEye: { canonical: ["RightEye"], aliases: ["EyeRight"] },
  leftUpperArm: { canonical: ["LeftArm"], aliases: ["LeftUpperArm"] },
  leftLowerArm: { canonical: ["LeftForeArm"], aliases: ["LeftLowerArm", "LeftForearm"] },
  leftHand: { canonical: ["LeftHand"], aliases: [] },
  rightUpperArm: { canonical: ["RightArm"], aliases: ["RightUpperArm"] },
  rightLowerArm: { canonical: ["RightForeArm"], aliases: ["RightLowerArm", "RightForearm"] },
  rightHand: { canonical: ["RightHand"], aliases: [] },
  leftUpperLeg: { canonical: ["LeftUpLeg"], aliases: ["LeftUpperLeg", "LeftThigh"] },
  leftLowerLeg: { canonical: ["LeftLeg"], aliases: ["LeftLowerLeg", "LeftCalf"] },
  leftFoot: { canonical: ["LeftFoot"], aliases: [] },
  rightUpperLeg: { canonical: ["RightUpLeg"], aliases: ["RightUpperLeg", "RightThigh"] },
  rightLowerLeg: { canonical: ["RightLeg"], aliases: ["RightLowerLeg", "RightCalf"] },
  rightFoot: { canonical: ["RightFoot"], aliases: [] }
};

const HUMANOID_ROLE_BY_BINDING = new Map(
  Object.entries(HUMANOID_ROLES).flatMap(([role, definition]) =>
    [...definition.canonical, ...definition.aliases].map(name => [normalizeAvatarBindingName(name), role])
  )
);
const AVATAR_FORWARD_AXES = new Set(["positive-z", "negative-z"]);
const forwardVector = new Vector3();
const rootWorldQuaternion = new Quaternion();
const headWorldQuaternion = new Quaternion();

function inferAvatarForwardAxis(root, bones, metadataRoot) {
  const declared = metadataRoot?.userData?.avatarCapabilities?.humanoid?.forwardAxis;
  if (AVATAR_FORWARD_AXES.has(declared)) return declared;
  if (!root || !bones.head) return "positive-z";

  root.updateWorldMatrix?.(true, true);
  root.getWorldQuaternion(rootWorldQuaternion).invert();
  bones.head.getWorldQuaternion(headWorldQuaternion).premultiply(rootWorldQuaternion);
  forwardVector.set(0, 0, 1).applyQuaternion(headWorldQuaternion);
  return forwardVector.z < 0 ? "negative-z" : "positive-z";
}

function isMixamoHumanoid(bones) {
  return Object.values(bones).some(bone => /^mixamorig/i.test(bone?.name || ""));
}

export function getAvatarRuntimeYawCorrection(root, metadataRoot = root) {
  const declaredForwardAxis = metadataRoot?.userData?.avatarCapabilities?.humanoid?.forwardAxis;
  if (!AVATAR_FORWARD_AXES.has(declaredForwardAxis)) return 0;

  const warnings = [];
  const bones = resolveHumanoidBones(root, sourceExtensionsFor(root), warnings, metadataRoot);
  return isMixamoHumanoid(bones) && declaredForwardAxis === "negative-z" ? Math.PI : 0;
}

const REQUIRED_HUMANOID_ROLES = [
  "hips",
  "spine",
  "neck",
  "head",
  "leftUpperArm",
  "leftLowerArm",
  "leftHand",
  "rightUpperArm",
  "rightLowerArm",
  "rightHand",
  "leftUpperLeg",
  "leftLowerLeg",
  "leftFoot",
  "rightUpperLeg",
  "rightLowerLeg",
  "rightFoot"
];

export function normalizeAvatarBindingName(value) {
  if (typeof value !== "string") return "";

  const localName = value.trim().replace(/^.*[:|/]/, "");
  return localName
    .replace(/^mixamorig[\s_.-]*/i, "")
    .replace(/[\s_.:\-|/]+/g, "")
    .toLowerCase();
}

function findNodeByUuid(root, uuid) {
  if (!root || !uuid) return null;
  if (root.uuid === uuid) return root;
  return root.getObjectByProperty?.("uuid", uuid) || null;
}

function isAvatarBone(node) {
  return node instanceof Bone || node?.isBone === true;
}

function isAnimationBindingBone(node) {
  return isAvatarBone(node) || node?.children?.some(child => isAvatarBone(child) && child.name === node.name);
}

function normalizedBoneTargets(root) {
  const targets = new Map();
  for (const bone of collectBones(root)) {
    const name = normalizeAvatarBindingName(bone.name);
    if (!name) continue;
    targets.set(name, targets.has(name) ? null : bone);
  }
  return targets;
}

function animationTrackTarget(root, track, normalizedBones) {
  if (!root || !track?.name) return null;

  let parsed;
  try {
    parsed = PropertyBinding.parseTrackName(track.name);
  } catch {
    return null;
  }

  const byUuid = findNodeByUuid(root, parsed.nodeName);
  if (byUuid) return byUuid;

  try {
    const exact = PropertyBinding.findNode(root, parsed.nodeName) || null;
    if (exact) return exact;
  } catch {
    return null;
  }

  return normalizedBones?.get(normalizeAvatarBindingName(parsed.nodeName)) || null;
}

function humanoidRoleTarget(track, bones) {
  let parsed;
  try {
    parsed = PropertyBinding.parseTrackName(track.name);
  } catch {
    return null;
  }
  const role = HUMANOID_ROLE_BY_BINDING.get(normalizeAvatarBindingName(parsed.nodeName));
  return role ? bones[role] || null : null;
}

function retargetedTrackName(target, parsed) {
  let name = target.uuid;
  if (parsed.objectName) {
    name += `.${parsed.objectName}`;
    if (parsed.objectIndex !== undefined) name += `[${parsed.objectIndex}]`;
  }
  name += `.${parsed.propertyName}`;
  if (parsed.propertyIndex !== undefined) name += `[${parsed.propertyIndex}]`;
  return name;
}

export function retargetAnimationClip(root, clip, metadataRoot = root) {
  const warnings = [];
  const bones = resolveHumanoidBones(root, sourceExtensionsFor(root), warnings, metadataRoot);
  const normalizedBones = normalizedBoneTargets(root);
  const tracks = [];

  for (const sourceTrack of clip?.tracks || []) {
    let parsed;
    try {
      parsed = PropertyBinding.parseTrackName(sourceTrack.name);
    } catch {
      continue;
    }
    const target = animationTrackTarget(root, sourceTrack, normalizedBones) || humanoidRoleTarget(sourceTrack, bones);
    if (!isAnimationBindingBone(target)) continue;
    const track = sourceTrack.clone();
    track.name = retargetedTrackName(target, parsed);
    tracks.push(track);
  }

  const retargeted = clip.clone();
  retargeted.tracks = tracks;
  retargeted.resetDuration();
  if (clip.duration === 0) retargeted.duration = 0;
  return retargeted;
}

function hasBindableAnimationTracks(root, clip, normalizedBones) {
  return (
    root &&
    Array.isArray(clip?.tracks) &&
    clip.tracks.some(track => isAnimationBindingBone(animationTrackTarget(root, track, normalizedBones)))
  );
}

export function canBindAnimationClip(root, clip) {
  if (!Number.isFinite(clip?.duration) || clip.duration <= 0) return false;
  return hasBindableAnimationTracks(root, clip, normalizedBoneTargets(root));
}

export function canBindAnimationSet(root, clips) {
  if (!root || !Array.isArray(clips) || clips.length === 0) return false;

  const normalizedBones = normalizedBoneTargets(root);
  return (
    clips.some(clip => Number.isFinite(clip?.duration) && clip.duration > 0) &&
    clips.every(
      clip =>
        Number.isFinite(clip?.duration) && clip.duration >= 0 && hasBindableAnimationTracks(root, clip, normalizedBones)
    )
  );
}

function collectBones(root) {
  const bones = [];
  if (!root) return bones;
  if (isAvatarBone(root)) bones.push(root);
  root.traverse?.(node => {
    if (node !== root && isAvatarBone(node)) bones.push(node);
  });
  return bones;
}

function uniqueBoneMatch(matches, role, warnings) {
  if (matches.length === 1) return matches[0];
  if (matches.length > 1) warnings.push(`Humanoid role ${role} has ambiguous normalized bone names.`);
  return null;
}

function resolveExplicitBone(value, bones, role, warnings) {
  if (isAvatarBone(value)) {
    if (bones.includes(value)) return value;
    warnings.push(`Humanoid role ${role} references a detached explicit bone.`);
    return null;
  }
  const reference = typeof value === "string" ? value : value?.uuid || value?.name;
  if (!reference) return null;

  const exactMatches = bones.filter(bone => bone.uuid === reference || bone.name === reference);
  const exact = uniqueBoneMatch(exactMatches, role, warnings);
  if (exact) return exact;

  return uniqueBoneMatch(
    bones.filter(bone => bone.userData?.name === reference),
    role,
    warnings
  );
}

function resolveHumanoidBones(root, sourceExtensions, warnings, metadataRoot = root) {
  const bones = collectBones(root);
  const explicitBones = metadataRoot?.userData?.avatarCapabilities?.humanoid?.bones || {};
  const resolved = {};

  for (const [role, definition] of Object.entries(HUMANOID_ROLES)) {
    let bone = resolveExplicitBone(explicitBones[role], bones, role, warnings);
    if (!bone) {
      for (const canonicalName of definition.canonical) {
        bone = uniqueBoneMatch(
          bones.filter(candidate => candidate.name === canonicalName),
          role,
          warnings
        );
        if (bone) break;
      }
    }
    if (!bone) {
      const aliases = [...definition.canonical, ...definition.aliases].map(normalizeAvatarBindingName);
      bone = uniqueBoneMatch(
        bones.filter(candidate => aliases.includes(normalizeAvatarBindingName(candidate.name))),
        role,
        warnings
      );
    }
    if (!bone) {
      const vrmBone = sourceExtensions?.VRMC_vrm?.humanoid?.humanBones?.[role];
      const gltfIndex = typeof vrmBone === "number" ? vrmBone : vrmBone?.node;
      if (Number.isInteger(gltfIndex)) {
        bone = uniqueBoneMatch(
          bones.filter(candidate => candidate.userData?.gltfIndex === gltfIndex),
          role,
          warnings
        );
      }
    }
    if (bone) resolved[role] = bone;
  }
  return resolved;
}

function resolveEmbeddedCapabilities(root, animations, warnings, metadataRoot = root) {
  const clips = Array.isArray(animations) ? animations.slice() : [];
  const declaredClipNames = metadataRoot?.userData?.avatarCapabilities?.animations?.clips || {};
  const semanticClips = {};
  const semanticClipNames = {};
  const missingRequired = [];
  const missingOptional = [];

  for (const [action, aliases] of Object.entries(ANIMATION_ALIASES)) {
    const hasDeclaredClip = Object.prototype.hasOwnProperty.call(declaredClipNames, action);
    const matches = hasDeclaredClip
      ? clips.filter(clip => clip?.name === declaredClipNames[action])
      : clips.filter(clip => aliases.map(normalizeAvatarBindingName).includes(normalizeAvatarBindingName(clip?.name)));
    for (const matched of matches) {
      if (canBindAnimationClip(root, matched)) {
        semanticClips[action] = matched;
        semanticClipNames[action] = matched.name;
        break;
      }
      warnings.push(`Embedded animation clip ${matched.name} cannot bind to a bone.`);
    }
    if (semanticClips[action]) continue;
    if (REQUIRED_ANIMATION_ACTIONS.includes(action)) missingRequired.push(action);
    else missingOptional.push(action);
  }
  return {
    allClips: clips,
    allClipNames: clips.map(clip => clip?.name).filter(Boolean),
    clips: semanticClips,
    clipNames: semanticClipNames,
    missingRequired,
    missingOptional,
    valid: missingRequired.length === 0
  };
}

function resolveLegacyCapabilities(root, legacyAnimations) {
  const profileClips = profile => {
    if (Array.isArray(legacyAnimations)) return legacyAnimations;
    if (!legacyAnimations || !Array.isArray(legacyAnimations[profile])) return null;
    return legacyAnimations[profile];
  };
  const female = profileClips("female");
  const male = profileClips("male");

  return {
    femaleCompatible: female === null ? null : canBindAnimationSet(root, female),
    maleCompatible: male === null ? null : canBindAnimationSet(root, male)
  };
}

function explicitAnimationSelection(root) {
  const metadata = root?.userData?.avatarCapabilities?.animations;
  if (metadata?.source === "embedded") return ANIMATION_SELECTIONS.EMBEDDED;
  if (metadata?.source === "legacy-bundled") {
    if (metadata.profile === "female") return ANIMATION_SELECTIONS.LEGACY_FEMALE;
    if (metadata.profile === "male") return ANIMATION_SELECTIONS.LEGACY_MALE;
    return null;
  }
  return metadata?.source === "none" ? ANIMATION_SELECTIONS.NONE : null;
}

function selectAnimationSource(root, embedded) {
  const metadata = root?.userData?.avatarCapabilities?.animations;
  if (metadata?.source === "embedded") {
    return embedded.valid ? ANIMATION_SELECTIONS.EMBEDDED : ANIMATION_SELECTIONS.NONE;
  }
  if (metadata?.source === "legacy-bundled") {
    if (metadata.profile === "female") return ANIMATION_SELECTIONS.LEGACY_FEMALE;
    if (metadata.profile === "male") return ANIMATION_SELECTIONS.LEGACY_MALE;
    return ANIMATION_SELECTIONS.NONE;
  }
  if (Object.prototype.hasOwnProperty.call(metadata || {}, "source")) return ANIMATION_SELECTIONS.NONE;
  if (root?.userData?.isMaleAnim) return ANIMATION_SELECTIONS.LEGACY_MALE;
  if (root?.userData?.isFemaleAnim) return ANIMATION_SELECTIONS.LEGACY_FEMALE;
  return embedded.valid ? ANIMATION_SELECTIONS.EMBEDDED : ANIMATION_SELECTIONS.NONE;
}

function metadataFor(report, selection) {
  switch (selection) {
    case ANIMATION_SELECTIONS.EMBEDDED:
      return {
        version: AVATAR_CAPABILITIES_VERSION,
        humanoid: {
          bones: report?.humanoid?.boneNames || {},
          forwardAxis: report?.humanoid?.forwardAxis || "positive-z"
        },
        animations: { source: "embedded", clips: { ...(report?.embedded?.clipNames || {}) } }
      };
    case ANIMATION_SELECTIONS.LEGACY_FEMALE:
      return {
        version: AVATAR_CAPABILITIES_VERSION,
        humanoid: {
          bones: report?.humanoid?.boneNames || {},
          forwardAxis: report?.humanoid?.forwardAxis || "positive-z"
        },
        animations: { source: "legacy-bundled", profile: "female" }
      };
    case ANIMATION_SELECTIONS.LEGACY_MALE:
      return {
        version: AVATAR_CAPABILITIES_VERSION,
        humanoid: {
          bones: report?.humanoid?.boneNames || {},
          forwardAxis: report?.humanoid?.forwardAxis || "positive-z"
        },
        animations: { source: "legacy-bundled", profile: "male" }
      };
    default:
      return {
        version: AVATAR_CAPABILITIES_VERSION,
        humanoid: {
          bones: report?.humanoid?.boneNames || {},
          forwardAxis: report?.humanoid?.forwardAxis || "positive-z"
        },
        animations: { source: "none" }
      };
  }
}
export function resolveAvatarCapabilities(root, animations = root?.animations || [], options = {}) {
  const warnings = [];
  const sourceExtensions = options.sourceExtensions || sourceExtensionsFor(options.sourceRoot || root);
  const metadataRoot = options.metadataRoot || root;
  const hasDeclaredAnimationSource = Object.prototype.hasOwnProperty.call(
    metadataRoot?.userData?.avatarCapabilities?.animations || {},
    "source"
  );
  const bones = resolveHumanoidBones(root, sourceExtensions, warnings, metadataRoot);
  const boneNames = Object.fromEntries(Object.entries(bones).map(([role, bone]) => [role, bone.name]));
  const missingRoles = REQUIRED_HUMANOID_ROLES.filter(role =>
    role === "spine" ? !bones.spine && !bones.chest : !bones[role]
  );
  const eyeAnchorMode = bones.leftEye && bones.rightEye ? "eyes" : bones.head ? "head" : "missing";
  const embedded = resolveEmbeddedCapabilities(root, animations, warnings, metadataRoot);
  const legacy = resolveLegacyCapabilities(root, options.legacyAnimations);
  const explicitSelection = explicitAnimationSelection(metadataRoot);
  const recommendedSelection = selectAnimationSource(metadataRoot, embedded);
  const humanoid = {
    bones,
    boneNames,
    missingRoles,
    eyeAnchorMode,
    forwardAxis: inferAvatarForwardAxis(root, bones, metadataRoot),
    valid: missingRoles.length === 0
  };
  const report = {
    embedded,
    legacy,
    fullBody: Boolean(bones.leftFoot || bones.rightFoot),
    humanoid,
    explicitSelection,
    hasDeclaredAnimationSource,
    metadata: null,
    recommendedSelection,
    warnings
  };

  report.metadata = metadataFor(report, recommendedSelection);
  return report;
}

export function buildRuntimeAnimationClips(report) {
  if (!report?.embedded?.valid) return {};

  const sourceClips = report.embedded.clips;
  const walkForward = sourceClips.walkForward;
  const idle = sourceClips.idle;
  const clipsByRuntimeName = {
    idle,
    walkForward,
    walking2: walkForward,
    walkBackward: sourceClips.walkBackward || walkForward,
    walkLeft: sourceClips.walkLeft || walkForward,
    walkRight: sourceClips.walkRight || walkForward,
    runForward: sourceClips.runForward || walkForward,
    runBackward: sourceClips.runBackward || walkForward,
    runLeft: sourceClips.runLeft || walkForward,
    runRight: sourceClips.runRight || walkForward,
    fly: sourceClips.fly || idle,
    sitLow: sourceClips.sitLow || idle,
    sitMedium: sourceClips.sitMedium || idle
  };

  return Object.fromEntries(
    Object.entries(clipsByRuntimeName).map(([action, clip]) => {
      const runtimeClip = clip.clone();
      runtimeClip.name = RUNTIME_ANIMATION_CLIP_NAMES[action];
      runtimeClip.userData = { ...runtimeClip.userData, avatarRuntimeSource: clip.uuid };
      return [runtimeClip.name, runtimeClip];
    })
  );
}

export function buildAvatarCapabilitiesMetadata(report, selection) {
  return metadataFor(report, selection);
}

export function getAvailableAnimationSelections(report) {
  const selections = [];
  if (report?.embedded?.valid) selections.push(ANIMATION_SELECTIONS.EMBEDDED);
  if (report?.legacy?.femaleCompatible) selections.push(ANIMATION_SELECTIONS.LEGACY_FEMALE);
  if (report?.legacy?.maleCompatible) selections.push(ANIMATION_SELECTIONS.LEGACY_MALE);
  selections.push(ANIMATION_SELECTIONS.NONE);
  return selections;
}

export function getPreviewAnimationClip(report, selection) {
  if (selection !== ANIMATION_SELECTIONS.EMBEDDED || !report?.embedded?.valid) return null;
  return report.embedded.clips.idle || null;
}

export function buildCustomAvatarExportOptions(report, selection) {
  if (selection === ANIMATION_SELECTIONS.EMBEDDED) {
    return { binary: true, animations: (report?.embedded?.allClips || []).slice() };
  }
  return { binary: true };
}

export function createCustomAvatarPreviewAnimationController({ nextTick = () => Promise.resolve() } = {}) {
  let generation = 0;
  let mixer = null;
  let action = null;

  const clear = () => {
    action?.stop();
    mixer?.stopAllAction();
    const animationRoot = mixer?.getRoot?.();
    if (animationRoot) mixer.uncacheRoot(animationRoot);
    mixer = null;
    action = null;
  };
  const start = (avatar, clip, startTime) => {
    mixer = new AnimationMixer(avatar);
    action = mixer.clipAction(clip);
    action.enabled = true;
    if (startTime > 0) action.time = startTime % clip.duration;
    action.setLoop(LoopRepeat, Infinity).play();
  };

  return {
    get action() {
      return action;
    },
    get mixer() {
      return mixer;
    },
    stop() {
      generation++;
      clear();
    },
    async play({ selection, report, avatar, legacyIdleAnimation, startTime = 0 }) {
      const token = ++generation;
      clear();

      if (selection === ANIMATION_SELECTIONS.NONE) return { status: "none" };

      const embeddedIdle = getPreviewAnimationClip(report, selection);
      if (embeddedIdle) {
        start(avatar, embeddedIdle, startTime);
        return { status: "playing" };
      }

      if (selection !== ANIMATION_SELECTIONS.LEGACY_FEMALE && selection !== ANIMATION_SELECTIONS.LEGACY_MALE) {
        return { status: "unavailable" };
      }
      if (!legacyIdleAnimation) return { status: "unavailable" };
      const retargetedIdle = retargetAnimationClip(avatar, legacyIdleAnimation);
      if (retargetedIdle.tracks.length === 0) return { status: "unavailable" };

      await nextTick();
      if (token !== generation) return { status: "stale" };

      start(avatar, retargetedIdle, startTime);
      return { status: "playing" };
    }
  };
}
export async function exportCustomAvatar(scene, options, exporter) {
  if (!exporter) throw new Error("A GLTFExporter is required.");
  return new Promise((resolve, reject) => {
    exporter.parse(
      scene,
      gltf => {
        if (gltf instanceof ArrayBuffer) {
          resolve(new Blob([gltf], { type: "model/gltf-binary" }));
        } else {
          reject(new Error("Exported data is not in binary format."));
        }
      },
      reject,
      { binary: true, ...options }
    );
  });
}
export async function exportCustomAvatarAtRest(scene, options, exporter, mixer, resumePreview) {
  if (mixer) {
    mixer.stopAllAction();
    const animationRoot = mixer.getRoot?.();
    if (animationRoot) mixer.uncacheRoot(animationRoot);
    scene.updateMatrixWorld(true);
  }
  try {
    return await exportCustomAvatar(scene, options, exporter);
  } finally {
    await resumePreview?.();
  }
}

function asBytes(buffer) {
  if (buffer instanceof ArrayBuffer) return new Uint8Array(buffer);
  if (ArrayBuffer.isView(buffer)) return new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  return null;
}

function avatarCapabilitiesMetadata(json) {
  const extras = [
    json?.extras,
    json?.asset?.extras,
    ...(json?.nodes || []).map(node => node?.extras),
    ...(json?.scenes || []).map(scene => scene?.extras)
  ];
  return extras
    .filter(value => Object.prototype.hasOwnProperty.call(value || {}, "avatarCapabilities"))
    .map(value => value.avatarCapabilities);
}

function normalizedJson(value) {
  if (Array.isArray(value)) return value.map(normalizedJson);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, child]) => [key, normalizedJson(child)])
    );
  }
  return value;
}

function sameJsonValue(left, right) {
  return JSON.stringify(normalizedJson(left)) === JSON.stringify(normalizedJson(right));
}

function verifyCustomAvatarJson(json, expectedClipNames, expectedCapabilities, errors) {
  const clipNames = new Set((json.animations || []).map(animation => animation?.name));
  for (const name of expectedClipNames || []) {
    if (!clipNames.has(name)) errors.push(`Missing animation clip ${name}.`);
  }
  if (!expectedCapabilities) return;

  const metadata = avatarCapabilitiesMetadata(json);
  if (typeof expectedCapabilities === "string") {
    if (
      !metadata.length ||
      !metadata.every(capabilities => capabilities?.animations?.source === expectedCapabilities)
    ) {
      errors.push(`Missing avatar capabilities source ${expectedCapabilities}.`);
    }
  } else if (!metadata.length || !metadata.every(capabilities => sameJsonValue(capabilities, expectedCapabilities))) {
    errors.push("Avatar capabilities metadata does not match the expected selection.");
  }
}

export function verifyCustomAvatarExport(buffer, expectedClipNames = [], expectedCapabilities) {
  const errors = [];
  try {
    const bytes = asBytes(buffer);
    if (!bytes || bytes.byteLength < 20) {
      errors.push("Invalid GLB buffer.");
      return { valid: false, errors };
    }

    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (view.getUint32(0, true) !== 0x46546c67 || view.getUint32(4, true) !== 2) {
      errors.push("Invalid GLB header.");
      return { valid: false, errors };
    }
    if (view.getUint32(8, true) !== bytes.byteLength) errors.push("Invalid GLB length.");

    const chunkLength = view.getUint32(12, true);
    if (view.getUint32(16, true) !== 0x4e4f534a || 20 + chunkLength > bytes.byteLength) {
      errors.push("Missing GLB JSON chunk.");
      return { valid: false, errors };
    }

    const json = JSON.parse(new TextDecoder().decode(bytes.subarray(20, 20 + chunkLength)).trim());
    verifyCustomAvatarJson(json, expectedClipNames, expectedCapabilities, errors);
  } catch {
    errors.push("Unable to parse GLB JSON.");
  }

  return { valid: errors.length === 0, errors };
}

export async function verifyCustomAvatarBlobExport(blob, expectedClipNames = [], expectedCapabilities) {
  const errors = [];
  try {
    if (!blob?.slice || !Number.isFinite(blob.size) || blob.size < 20) {
      errors.push("Invalid GLB buffer.");
      return { valid: false, errors };
    }

    const header = new Uint8Array(await blob.slice(0, 20).arrayBuffer());
    const view = new DataView(header.buffer, header.byteOffset, header.byteLength);
    if (view.getUint32(0, true) !== 0x46546c67 || view.getUint32(4, true) !== 2) {
      errors.push("Invalid GLB header.");
      return { valid: false, errors };
    }
    if (view.getUint32(8, true) !== blob.size) errors.push("Invalid GLB length.");

    const chunkLength = view.getUint32(12, true);
    if (view.getUint32(16, true) !== 0x4e4f534a || 20 + chunkLength > blob.size) {
      errors.push("Missing GLB JSON chunk.");
      return { valid: false, errors };
    }

    const jsonBytes = new Uint8Array(await blob.slice(20, 20 + chunkLength).arrayBuffer());
    const json = JSON.parse(new TextDecoder().decode(jsonBytes).trim());
    verifyCustomAvatarJson(json, expectedClipNames, expectedCapabilities, errors);
  } catch {
    errors.push("Unable to parse GLB JSON.");
  }

  return { valid: errors.length === 0, errors };
}

export function copyAvatarSourceExtensions(sourceRoot, targetRoot) {
  const extensions = sourceExtensionsFor(sourceRoot);
  if (targetRoot && extensions) {
    targetRoot.traverse?.(node => sourceExtensionsByRoot.set(node, extensions));
    sourceExtensionsByRoot.set(targetRoot, extensions);
  }
  return targetRoot;
}

export function registerAvatarSourceMetadata(root, json) {
  if (root?.userData?.avatarCapabilities) return root;
  const sources = [
    ...(json?.scenes || []).map(scene => scene.extras),
    ...(json?.nodes || []).map(node => node.extras),
    json?.asset?.extras,
    json?.extras
  ];
  const metadata = sources.map(extras => extras?.avatarCapabilities).find(Boolean);
  if (metadata) root.userData.avatarCapabilities = metadata;
  return root;
}

export function registerAvatarSourceExtensions(root, extensions) {
  if (root && extensions) {
    root.traverse?.(node => sourceExtensionsByRoot.set(node, extensions));
    sourceExtensionsByRoot.set(root, extensions);
  }
  return root;
}
